import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { ModalService } from '../lib/modal.service';
import { ValidatorFn, FormGroup, ValidationErrors } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { ORG_CONFIG } from './constants';

@Injectable()
export class UtilityService {
    assignDevDisclaimerSubject = new BehaviorSubject<boolean>(false);
    private userDetailsPageDayFilterSubject = new BehaviorSubject<any>(30);
    userDetailsPageDayFilter =
        this.userDetailsPageDayFilterSubject.asObservable();
    constructor(
        private modalService: ModalService,
        private http: HttpClient
    ) {}
    public static strToDate(str: string): Date {
        str = str?.replace(/\s/i, 'T');
        str = str?.replace(/\..*$/i, 'Z');
        return new Date(str); //UTC hack
    }

    public static convertToDate(date: any): Date {
        if (date instanceof Date) {
            return date;
        } else if (date?.date) {
            return UtilityService.strToDate(date.date);
        } else {
            return UtilityService.strToDate(date);
        }
    }

    public static getSubdomain(): string {
        let full = window.location.host;

        let parts = full.split('.');
        let sub;
        if (parts.length > 2) {
            sub = parts[0];
        } else {
            sub = '';
        }

        return sub;
    }

    public static getAccessCode(): string {
        return '';
    }

    toQueryStringParams(obj: any): string {
        let str = [];

        for (let p in obj) {
            if (obj.hasOwnProperty(p)) {
                str.push(
                    encodeURIComponent(p) + '=' + encodeURIComponent(obj[p])
                );
            }
        }

        return str.join('&');
    }

    capitalizeFirstLetter(str: string): string {
        return str[0].toUpperCase() + str.slice(1);
    }

    public static capitalizeFirstCharacter(str: string): string {
        return str[0].toUpperCase() + str.slice(1);
    }

    strRandom(length: number) {
        let result = '';
        const characters =
            'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const charactersLength = characters.length;
        for (let i = 0; i < length; i++) {
            result += characters.charAt(
                Math.floor(Math.random() * charactersLength)
            );
        }
        return result;
    }

    demoMode() {
        if (environment.demo) {
            this.modalService.showAlert(
                'Demo mode',
                'Any account updates have been disabled on this demo site.'
            );
            return true;
        } else {
            return false;
        }
    }

    isDemoMode() {
        if (environment.demo) {
            return true;
        } else {
            return false;
        }
    }

    urlValidationPattern() {
        return (
            '^(https?:\\/\\/)' + // protocol
            '((([a-z\\d]([a-zA-Z\\d-]*[a-z\\d])*)\\.)+[a-zA-Z]{2,}|' + // domain name
            '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
            '(\\:\\d+)?(\\/[-a-zA-Z\\d%_.~+]*)*' + // port and path
            '(\\?[;&a-zA-Z\\d%_.~+=-]*)?' + // query string
            '(\\#[-a-zA-Z\\d_]*)?$'
        );
    }

    passwordValidation(password: string) {
        const regex: RegExp = /[!@#$%^&*(),.?":{}|<>]/;
        return {
            lowerLetters: /[a-z]+/.test(password),
            upperLetters: /[A-Z]+/.test(password),
            numbers: /[0-9]+/.test(password),
            symbols: regex.test(password),
            pwdlength: password.length >= 8 && password.length <= 32,
            notIncludeWelltrack: !password.toLowerCase().includes('welltrack'),
        };
    }

    commaSeparatedUrlPattern() {
        const urlPattern =
            '(http[s]?:\\/\\/){0,1}(www\\.){0,1}[a-zA-Z0-9\\.\\-]+\\.[a-zA-Z]{2,5}(\\/[a-zA-Z0-9\\.\\-]+)*[\\/]{0,1}';
        return '^' + urlPattern + '(,[ ]*' + urlPattern + ')*[\\,]{0,1}[ ]*$';
        // return  "^(" + urlPattern + "(,[ ]*))*$";
    }

    phoneValidationPattern() {
        // return '^(([(])?[0-9]{3}([)])?([ |-])?[0-9]{3}[-]?[0-9]{4}(,,[0-9],,[0-9],[0-9])?)|(\\+\\d{1,14})$'; // conbind the old and new pattern E.164
        return '^(([(])?[0-9]{3}([)])?([ |-])?[0-9]{3}[-]?[0-9]{4}(,,[0-9],,[0-9],[0-9])?)|([0-9]{3,})|(\\+\\d{1,14})$';
    }

    emailValidationPattern() {
        return "^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,4}$";
    }

    validateEmail(value) {
        let reg =
            /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
        var RegularExp = new RegExp(reg);
        if (RegularExp.test(value)) {
            return true;
        } else {
            return false;
        }
    }

    atLeastOneValidator(keys: string[]): ValidatorFn {
        return (control: FormGroup): ValidationErrors | null => {
            const valid = keys.some(key => control.get(key).value);
            return valid ? null : { atLeastOne: true };
        };
    }
    async loadImageFromAsset(imagePath: string): Promise<string> {
        const imageBlob = await this.http
            .get(imagePath, { responseType: 'blob' })
            .toPromise();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(imageBlob);
        });
    }

    // calculatePercentageChange(initial, recent) {
    //     return ((recent - initial) / initial) * 100;
    // }

    calculatePercentageChange(initial: number, recent: number): string {
        if (initial === 0) {
            if (recent === 0) {
                return '0';
            }
            return '100';
        }
        const change = ((recent - initial) / initial) * 100;
        const cappedChange = Math.min(change, 100);
        return cappedChange.toFixed(2);
    }

    calculateDifference(initial, recent) {
        return recent - initial;
    }

    getPercentageChanges(first, last) {
        const initialDateObj = first;
        const mostRecentDateObj = last;

        if (initialDateObj && mostRecentDateObj) {
            const initialMentalWellBeing = Math.round(
                initialDateObj.MetricScore
            );
            const recentMentalWellBeing = Math.round(
                mostRecentDateObj.MetricScore
            );
            const mentalWellBeingChange =
                initialMentalWellBeing > 0 || recentMentalWellBeing > 0
                    ? this.calculatePercentageChange(
                          initialMentalWellBeing,
                          recentMentalWellBeing
                      )
                    : 0;
            const mentalWellBeingDifference =
                initialMentalWellBeing >= 0 && recentMentalWellBeing >= 0
                    ? this.calculateDifference(
                          initialMentalWellBeing,
                          recentMentalWellBeing
                      )
                    : null;
            return {
                mentalWellBeingChange,
                mentalWellBeingDifference,
            };
        } else {
            return null;
        }
    }

    setAssignDevDisclaimerSubject(value: boolean) {
        this.assignDevDisclaimerSubject.next(value);
    }

    getInitials(name: string): string {
        if (!name) return '';
        const names = name.split(' ');
        const firstNameInitial = names[0] ? names[0][0] : '';
        const lastNameInitial =
            names.length > 1 ? names[names.length - 1][0] : '';
        return `${firstNameInitial}${lastNameInitial}`;
    }
    setUserDetailsPageDayFilterSubject(value: any) {
        this.userDetailsPageDayFilterSubject.next(value);
    }
    truncateTextToTwoLines(text: string): string {
        const sentences = text.match(/[^.!?]+[.!?]/g) || [];
        const hasMoreSentences = sentences.length > 2;
        const firstTwoSentences = sentences.slice(0, 2).join(' ');
        return firstTwoSentences + (hasMoreSentences ? '...' : '');
    }

    public static getLighterColor(hex: string, percent: number): string {
        let r = parseInt(hex.substring(1, 3), 16);
        let g = parseInt(hex.substring(3, 5), 16);
        let b = parseInt(hex.substring(5, 7), 16);

        r = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)));
        g = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)));
        b = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)));

        return `#${r.toString(16).padStart(2, '0')}${g
            .toString(16)
            .padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    }

    public static getTextColorBlackOrWhite(hex: string): string {
        let r = parseInt(hex.substring(1, 3), 16);
        let g = parseInt(hex.substring(3, 5), 16);
        let b = parseInt(hex.substring(5, 7), 16);

        let luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

        return luminance > 0.7 ? '#000000' : '#FFFFFF';
    }

    public static getDarkerColor(hex: string, percent: number): string {
        let r = parseInt(hex.substring(1, 3), 16);
        let g = parseInt(hex.substring(3, 5), 16);
        let b = parseInt(hex.substring(5, 7), 16);

        r = Math.max(0, Math.floor(r * (1 - percent / 100)));
        g = Math.max(0, Math.floor(g * (1 - percent / 100)));
        b = Math.max(0, Math.floor(b * (1 - percent / 100)));

        return `#${r.toString(16).padStart(2, '0')}${g
            .toString(16)
            .padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    }

    getOwlCarouselOptionsObject() {
        return {
            loop: false,
            navSpeed: 200,
            dots: false,
            freeDrag: false,
            pullDrag: false,
            mouseDrag: true,
            navText: [
                `<span aria-label="Previous" role="button" tabindex="0">
                    <i class="fa fa-angle-left"></i>
                </span>`,
                `<span aria-label="Next" role="button" tabindex="0">
                    <i class="fa fa-angle-right"></i>
                </span>`,
            ],
            autoWidth: true,
            margin: 0,
            items: 7.5,
            slideBy: 3,
            responsive: {
                0: { items: 1, slideBy: 1 },
                400: { items: 2, slideBy: 1 },
                500: { items: 2.5, slideBy: 1 },
                600: { items: 3, slideBy: 2 },
                700: { items: 3.5, slideBy: 2 },
                800: { items: 4, slideBy: 3 },
                900: { items: 4.5, slideBy: 3 },
                1000: { items: 5, slideBy: 4 },
                1100: { items: 5.5, slideBy: 4 },
                1200: { items: 6, slideBy: 4 },
                1300: { items: 6.5, slideBy: 4 },
                1400: { items: 7, slideBy: 5 },
                1500: { items: 7.5, slideBy: 5 },
                1600: { items: 8, slideBy: 5 },
                1700: { items: 8.5, slideBy: 5 },
                1800: { items: 9, slideBy: 5 },
            },
            nav: true,
            lazyLoad: true,
        };
    }

    getCustomOrgTextKey(orgId: number): string {
        let customOrgTextKey =
            ORG_CONFIG.CUSTOM_ORG_LIST.find(org => org.id === orgId)?.key || '';

        return customOrgTextKey;
    }

    isSmsSupported(): boolean {
        const ua = navigator.userAgent;

        const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);

        const isMac = /Macintosh|Mac OS X/i.test(ua);

        return isMobile || isMac;
    }
}
